import { Component, OnInit, inject } from '@angular/core';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { RestService } from '../services/rest.service';
import { Router } from '@angular/router';
import { SessionService, isEntitled, isPaidUser } from '../services/session.service';
import { LoginService } from '../services/login.service';
import { cloneDeep } from 'lodash';
import { BrandService } from '../../services/brand-config.service';
import { TranslationService, PickerLanguage } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { MaccabiService } from '../services/maccabi.service';
import { AppProfileService } from '../services/app-profile.service';
import { RoutineService } from '../services/routine.service';
import { RoutineReminderService } from '../services/routine-reminder.service';
import { CompletionService } from '../services/completion.service';
import { RoutineSchedule, TOOLS } from '../shell/shell.models';
import { PushNotificationService } from '../services/push-notification.service';
import { MarketService } from '../services/market.service';
import { environment } from '../../environments/environment';
import { AppDownloadService } from '../services/app-download.service';
import { StoreBillingService } from '../services/store-billing.service';
import { AppDownloadCardComponent } from '../shared/components/app-download/app-download-card.component';
import { PrivacySettingsComponent } from './privacy-settings/privacy-settings.component';

@Component({
  selector: 'app-user-account',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    DatePipe,
    TranslatePipe,
    MatMenuModule,
    AppDownloadCardComponent,
    PrivacySettingsComponent,
  ],
  templateUrl: './user-account.component.html',
  styleUrls: ['./user-account.component.scss'],
})
export class UserAccountComponent implements OnInit {
  restService = inject(RestService);
  private appDownload = inject(AppDownloadService);
  user: any = null;

  /** The app-download row is Markable-web only (app-download spec §4). */
  /** On a computer the profile offer is the full card (it brings its own
   *  frame), so the section must not wrap it in a second card. */
  get appDownloadIsCard(): boolean {
    return this.appDownload.isDesktop;
  }

  get showAppDownload(): boolean {
    return this.appDownload.available;
  }

  // The picker's own view (en | he | en-GB) so the active English variant
  // (US vs UK) highlights correctly. Kept in sync with the translation service.
  pickerLang: PickerLanguage = 'en';

  isScansLoading = false;
  doctors: any[] = [];

  // Pending-locked scans ("בדיקות שממתינות לפתיחה"): completed scans the account
  // hasn't unlocked yet. Server-derived from live entitlement (never a stored
  // flag), keyed to the logged-in user. Empty for entitled accounts.
  lockedScans: { guid: string; created: string }[] = [];

  // subscription
  remainingTests = 0;

  // monthly stats
  activeDays = 0;
  streak = 0;
  completionsThisMonth = 0;

  // routine (notification settings)
  schedule: RoutineSchedule = {};
  tools = TOOLS;
  freqOpts = ['daily', '2-3x', 'weekly'];
  timeOpts = ['anytime', 'morning', 'midday', 'evening'];

  savingProfile = false;

  // Test-notification button — staging/dev only (hidden in production builds).
  showTestNotif = !environment.production;
  testingNotif = false;
  notifStatus = '';
  notifBlocked = false;

  restoring = false;
  restoreMessage = '';

  // Account deletion (V-04): scheduled with a 14-day grace, cancellable.
  deletionBusy = false;
  deletionScheduledFor: string | null = null;
  deletionError = '';

  constructor(
    public sessionService: SessionService,
    public brandService: BrandService,
    public translationService: TranslationService,
    private router: Router,
    private maccabiService: MaccabiService,
    private profileSvc: AppProfileService,
    private routineSvc: RoutineService,
    private reminders: RoutineReminderService,
    private completionSvc: CompletionService,
    private loginService: LoginService,
    private pushSvc: PushNotificationService,
    private marketService: MarketService,
    public storeBilling: StoreBillingService,
  ) {
    this.user = cloneDeep(this.sessionService.session.user);
    // Track the PICKER language so the profile picker highlights US vs UK vs
    // Hebrew and shows the right label (from i18n, not a hardcoded string).
    this.translationService.pickerLanguage$.subscribe((lang) => {
      this.pickerLang = lang;
    });
  }

  // The label shown on the closed picker row, always from the i18n file (never
  // a hardcoded "English"). India shows a single neutral English; otherwise the
  // active variant is US / UK / Hebrew.
  get currentLangLabelKey(): string {
    if (this.isIndiaMarket) return 'miscUi.langEnglishOnly';
    if (this.pickerLang === 'he') return 'miscUi.langHebrew';
    if (this.pickerLang === 'en-GB') return 'miscUi.langEnglishUk';
    return 'miscUi.langEnglish';
  }

  // India market: the language picker shows English only (Hebrew hidden) and the
  // account can never resolve to plain en.json or he.json (DMRA prohibition §3).
  get isIndiaMarket(): boolean {
    return this.marketService.isIndia();
  }

  // Staging-only: full round-trip test. Native sends a real FCM push; web
  // shows a browser notification (after a server round-trip). Busy-guarded.
  async sendTestNotification(): Promise<void> {
    if (this.testingNotif) return;
    this.testingNotif = true;
    this.notifStatus = '';
    try {
      const title = this.t('shell.profile.testNotif.title');
      const body = this.t('shell.profile.testNotif.payload');
      const res = await this.pushSvc.sendTest(title, body);
      if (res.ok || res.detail.startsWith('web-shown')) {
        this.notifStatus = this.t('shell.profile.testNotif.ok');
      } else if (res.detail.startsWith('permission')) {
        // denied or unsupported (e.g. iOS WebView / no Notification API):
        // surface a clear message and disable the button so it can't keep
        // failing — the user must enable notifications at the OS level.
        this.notifStatus = this.t('shell.profile.testNotif.denied');
        this.notifBlocked = true;
      } else {
        this.notifStatus = this.t('shell.profile.testNotif.error');
      }
    } catch (e) {
      console.error('sendTestNotification failed:', e);
      this.notifStatus = this.t('shell.profile.testNotif.error');
    } finally {
      this.testingNotif = false;
    }
  }

  // Admin link + logout moved here from the (now removed) hamburger header.
  get isAdmin(): boolean {
    return !!this.sessionService.session?.user?.isAdmin;
  }

  // --- Paywall v2 locked states (locked-flow spec §Profile, lines 115-119,
  // 144-146, 157-158). Unpaid accounts see locked/teaser variants of the
  // monthly summary, subscription card and test history; notifications follow
  // the tools flag (locked only in Option A). Paid users see the real thing.
  // Unified entitlement (paid OR admin OR admin-granted unused credit). The
  // whole profile — "your plan", monthly stats, full scan history — reflects
  // this, so admins + granted-credit users see the paid/entitled treatment, not
  // the free state (BUG-ENTITLEMENT-NOT-RESOLVED).
  get isEntitled(): boolean {
    return isEntitled(this.sessionService.session?.user as any);
  }

  // A paid term (store or web subscription/pass) or clinic access = unlimited
  // scans until it ends, so the count of single-scan credits does not apply.
  get hasPaidTerm(): boolean {
    return isPaidUser(this.sessionService.session?.user as any);
  }

  get isMaccabi(): boolean {
    return !!this.sessionService.session?.user?.isMaccabi;
  }

  // Option A (freeTierToolsLocked) sells the tools/routine; Option B leaves
  // them open. Notifications ship with the tools, so they lock only in A.
  get toolsLocked(): boolean {
    return !!this.sessionService.session?.user?.freeTierToolsLocked;
  }

  get notifLocked(): boolean {
    return !this.isEntitled && this.toolsLocked;
  }

  // Monthly-teaser copy branches on the tools flag (§141): Option A invites her
  // to open the tools; Option B says the numbers are gathered from them.
  get monthlyTeaserKey(): string {
    return this.toolsLocked
      ? 'profile.locked.monthlyTeaserLocked'
      : 'profile.locked.monthlyTeaserOpen';
  }

  // Test history shows only the first (free) scan for non-entitled accounts
  // (§119): the earliest completed scan if any, else the earliest scan. Entitled
  // users (paid / admin / admin-granted credit) see ALL their scans — this is
  // the BUG-PROFILE-MISSING-SCANS fix (the gate was the narrow paid flag).
  get visibleScans(): any[] {
    const scans = this.user?.scans || [];
    if (this.isEntitled || scans.length <= 1) return scans;
    const sorted = [...scans].sort((a: any, b: any) => {
      const ta = a.created ? new Date(a.created).getTime() : 0;
      const tb = b.created ? new Date(b.created).getTime() : 0;
      return ta - tb;
    });
    const firstCompleted = sorted.find((s: any) => s.status === 'Completed');
    return [firstCompleted || sorted[0]];
  }

  /** Newest first: what she reads first is what she did last. */
  private byNewest(scans: any[]): any[] {
    return [...scans].sort((a: any, b: any) => {
      const ta = a.created ? new Date(a.created).getTime() : 0;
      const tb = b.created ? new Date(b.created).getTime() : 0;
      return tb - ta;
    });
  }

  /** The mappings she finished. These are the rows that lead somewhere. */
  get completedScans(): any[] {
    return this.byNewest(this.visibleScans.filter((s: any) => s.status === 'Completed'));
  }

  /**
   * The ones she opened and did not finish. They used to be listed one under
   * the other and filled the card with a dozen identical Pending rows from the
   * same day (Danna, 17.09), so they are folded into one line she can open.
   */
  get unfinishedScans(): any[] {
    return this.byNewest(this.visibleScans.filter((s: any) => s.status !== 'Completed'));
  }

  /** At most the latest ten inside the fold; older ones are counted. */
  get unfinishedShown(): any[] {
    return this.unfinishedScans.slice(0, 10);
  }

  get unfinishedHidden(): number {
    return Math.max(0, this.unfinishedScans.length - this.unfinishedShown.length);
  }

  // The FIRST (earliest) completed scan — the free "initial review" that is
  // always available. The plan card's secondary button opens THIS (free), not
  // the paywall (N57b-4).
  get firstScanGuid(): string | null {
    const completed = (this.user?.scans || []).filter(
      (s: any) => s?.status === 'Completed' && s?.guid,
    );
    if (!completed.length) return null;
    const sorted = [...completed].sort((a: any, b: any) => {
      const ta = a.created ? new Date(a.created).getTime() : 0;
      const tb = b.created ? new Date(b.created).getTime() : 0;
      return ta - tb;
    });
    return sorted[0].guid;
  }

  viewFirstResults(): void {
    const g = this.firstScanGuid;
    if (g) this.navigateToResults(g);
  }

  // Open the consumer paywall ("התמונה המלאה"); pass the latest completed scan
  // as context so the one-time/subscription flow has a scanId (mirrors home).
  openPaywall(): void {
    const scans = this.user?.scans || [];
    const completed = scans.filter((s: any) => s.status === 'Completed');
    const ctx = completed.length ? completed[completed.length - 1].guid : undefined;
    this.router.navigate(['/' + this.currentLang + '/app/payment'], {
      queryParams: { scanId: ctx },
    });
  }

  navigateToAdmin(): void {
    this.router.navigate(['/', this.translationService.getCurrentLanguage(), 'admin', 'scan']);
  }

  logout(): void {
    this.loginService.logout();
  }

  ngOnInit(): void {
    void this.loadDeletionStatus();
    void this.syncAccount();
    this.getScans();
    this.loadLockedScans();
    this.getDoctors();
    this.loadStats();
    this.loadRoutine();
  }

  /**
   * The session user is cached at login and never re-read, so access granted
   * AFTER that login was invisible here: a woman who joined a clinic that gives
   * her free tests kept seeing the free plan card, and "the full picture" sent
   * her to the payment page although her tests were already paid for. Re-read
   * the account first, then paint the plan from it. Falls back to the cached
   * session when the refresh fails, so the profile always renders.
   */
  private async syncAccount(): Promise<void> {
    const fresh = await this.sessionService.refreshUser();
    if (fresh) {
      const scans = this.user?.scans;
      this.user = cloneDeep(fresh);
      if (scans && !this.user.scans) this.user.scans = scans;
    }
    this.computeRemaining();
  }

  // Pending-locked scans for the "בדיקות שממתינות לפתיחה" section. Server decides
  // locked/unlocked per user; entitled accounts get an empty list (section hides).
  private async loadLockedScans(): Promise<void> {
    try {
      const resp: any = await this.restService.request({
        path: 'scan/me/pending-locked',
        method: 'GET',
        throwError: true,
      });
      this.lockedScans = resp?.scans ?? [];
    } catch (e) {
      console.error('user-account: loadLockedScans error:', e);
      this.lockedScans = [];
    }
  }

  t(key: string): string {
    return this.translationService.translate(key);
  }

  get currentLang(): string {
    return this.translationService.getCurrentLanguage();
  }

  // Switch UI language from the profile picker: en | en-GB | he.
  // en-GB is an English VARIANT — it changes the loaded locale (en-GB.json) and
  // the picker highlight, but NOT market/currency/price (spec B3) and NOT the
  // URL segment (it rides /en). The account stores only the collapsed app
  // language (en-GB -> en), matching the server whitelist + SupportedLanguage;
  // the en-GB variant itself persists locally (localStorage app_language).
  // Compare against the PICKER language (not currentLang) so switching between
  // English (US) and English (UK) — which share the /en app language — works.
  async switchLang(lang: PickerLanguage): Promise<void> {
    if (lang === this.pickerLang) return;
    await this.translationService.changeLanguage(lang);
    const accountLang: 'he' | 'en' = lang === 'he' ? 'he' : 'en';
    // Persist to the account so the choice follows the user across web/native/
    // reinstall and is no longer overridable by a /en|/he URL segment.
    this.profileSvc.updateProfile({ language: accountLang }).catch((e) => {
      console.error('persist language failed:', e);
    });
    // en-GB never appears in the URL — it rides on the /en segment.
    const urlLang = lang === 'he' ? 'he' : 'en';
    const segs = this.router.url.split('/').filter((s) => s);
    if (segs[0] === 'he' || segs[0] === 'en') {
      segs[0] = urlLang;
      await this.router.navigateByUrl('/' + segs.join('/'));
    } else {
      await this.router.navigateByUrl('/' + urlLang + '/' + segs.join('/'));
    }
  }

  toolName(id: string): string {
    return this.translationService.translate('shell.tools.' + id + '.name');
  }

  /** B13: when her next reminder for this tool lands, in plain language
   *  ("every day in the morning"), composed from the frequency + time she
   *  picked. Empty when the tool is off, so nothing is promised. */
  nextReminderText(id: string): string {
    const cfg = this.schedule[id];
    if (!cfg?.active) return '';
    const t = (k: string) => {
      const v = this.translationService.translate(k);
      return v && v !== k ? v : '';
    };
    const freq = t('shell.profile.nextReminderFreq.' + (cfg.freq || 'daily'));
    const time = cfg.time && cfg.time !== 'anytime'
      ? t('shell.profile.nextReminderTime.' + cfg.time)
      : '';
    const when = [freq, time].filter(Boolean).join(' ');
    if (!when) return '';
    return this.translationService.translate('shell.profile.nextReminder', { when });
  }

  private computeRemaining(): void {
    const now = Date.now();
    const entries = this.user?.creditEntries || [];
    this.remainingTests = entries
      .filter((e: any) => !e.expirationDate || new Date(e.expirationDate).getTime() > now)
      .reduce((sum: number, e: any) => sum + (e.amount || 0), 0);
  }

  private async loadStats(): Promise<void> {
    try {
      const completions = await this.completionSvc.getRange();
      const eng = this.completionSvc.engagement(completions);
      this.activeDays = eng.activeDays;
      this.streak = this.completionSvc.streak(completions);
      const now = new Date();
      const ym = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
      this.completionsThisMonth = completions.filter((c) => c.date.startsWith(ym)).length;
    } catch (e) {
      console.error('profile stats failed:', e);
    }
  }

  private async loadRoutine(): Promise<void> {
    try {
      this.schedule = await this.routineSvc.getSchedule(true);
      void this.reminders.sync(this.schedule);
    } catch (e) {
      console.error('profile routine failed:', e);
    }
  }

  isToolActive(id: string): boolean {
    return !!this.schedule[id]?.active;
  }

  async toggleToolActive(id: string): Promise<void> {
    const cur = this.schedule[id];
    if (cur?.active) {
      this.schedule[id] = { ...cur, active: false };
    } else {
      this.schedule[id] = {
        active: true,
        freq: cur?.freq || 'daily',
        time: cur?.time || 'anytime',
      };
    }
    await this.saveRoutine();
  }

  async setFreq(id: string, freq: string): Promise<void> {
    if (!this.schedule[id]) return;
    this.schedule[id] = { ...this.schedule[id], freq };
    await this.saveRoutine();
  }

  async setTime(id: string, time: string): Promise<void> {
    if (!this.schedule[id]) return;
    this.schedule[id] = { ...this.schedule[id], time };
    await this.saveRoutine();
  }

  private async saveRoutine(): Promise<void> {
    try {
      await this.routineSvc.saveSchedule(this.schedule);
      // The screen promises "Next reminder: ..." — hand the schedule to the OS
      // so that promise is actually kept (native only).
      await this.reminders.sync(this.schedule);
    } catch (e) {
      console.error('save routine failed:', e);
    }
  }

  async getScans() {
    this.isScansLoading = true;
    try {
      const response: any = await this.restService.request({
        path: `scan/user/${this.sessionService.session.user.id}`,
        method: 'GET',
        throwError: true,
      });
      this.user.scans = response.scans;
    } catch (error) {
      console.error('user-account: getScans error:', error);
    } finally {
      this.isScansLoading = false;
    }
  }

  async getDoctors() {
    try {
      const response = await this.maccabiService.getMyDoctors();
      this.doctors = response?.doctors || [];
    } catch {
      this.doctors = [];
    }
  }

  async removeDoctor(linkId: number) {
    const ok = window.confirm(
      this.translationService.translate('myDoctors.removeConfirm'),
    );
    if (!ok) return;
    await this.maccabiService.removeDoctor(linkId);
    await this.getDoctors();
  }

  // Identity save via the shell profile endpoint (keeps fname/lname/idNumber).
  async saveIdentity(): Promise<void> {
    if (this.savingProfile) return;
    this.savingProfile = true;
    try {
      await this.profileSvc.updateProfile({
        fname: this.user.fname,
        lname: this.user.lname,
        idNumber: this.user.idNumber,
      });
    } catch (e) {
      console.error('saveIdentity failed:', e);
    } finally {
      this.savingProfile = false;
    }
  }

  async toggleContraception(checked: boolean): Promise<void> {
    this.user.contraception = checked ? 'yes' : 'no';
    try {
      await this.profileSvc.updateProfile({ contraception: this.user.contraception });
    } catch (e) {
      console.error('toggleContraception failed:', e);
    }
  }

  get contraceptionOn(): boolean {
    return this.user?.contraception === 'yes';
  }

  getStatusTranslation(status: string): string {
    if (status === 'Pending') return this.t('userAccount.statusPending');
    if (status === 'Completed') return this.t('userAccount.statusCompleted');
    return status;
  }

  startNewAnalysis() {
    if (!this.sessionService.session.user.purpose) {
      this.router.navigate(['/app/purpose']);
    } else {
      this.router.navigate(['/app/new-test']);
    }
  }
  navigateToResults(guid: string) {
    this.router.navigate(['/app/results', guid]);
  }
  navigateToDoctorReport(guid: string) {
    this.router.navigate(['/app/doctor-report', guid]);
  }
  navigateToTest(guid: string) {
    this.router.navigate(['/app/test'], { queryParams: { scanId: guid } });
  }
  async loadDeletionStatus(): Promise<void> {
    try {
      const res: any = await this.restService.request({
        path: 'user/me/deletion',
        method: 'GET',
        skipAuthRedirect: true,
      });
      this.deletionScheduledFor = res?.deletion?.scheduledFor || null;
    } catch {
      this.deletionScheduledFor = null;
    }
  }

  async requestAccountDeletion(): Promise<void> {
    if (this.deletionBusy) return;
    if (!confirm(this.t('shell.profile.deleteConfirm'))) return;
    this.deletionBusy = true;
    this.deletionError = '';
    try {
      const res: any = await this.restService.request({
        path: 'user/me/delete',
        method: 'POST',
        throwError: true,
      });
      this.deletionScheduledFor = res?.deletion?.scheduledFor || null;
    } catch (e) {
      console.error('requestAccountDeletion failed:', e);
      this.deletionError = this.t('shell.profile.deleteError');
    } finally {
      this.deletionBusy = false;
    }
  }

  async cancelAccountDeletion(): Promise<void> {
    if (this.deletionBusy) return;
    this.deletionBusy = true;
    this.deletionError = '';
    try {
      await this.restService.request({
        path: 'user/me/deletion/cancel',
        method: 'POST',
        throwError: true,
      });
      this.deletionScheduledFor = null;
    } catch (e) {
      console.error('cancelAccountDeletion failed:', e);
      this.deletionError = this.t('shell.profile.deleteError');
    } finally {
      this.deletionBusy = false;
    }
  }
  navigateToDailyCheckin() {
    this.router.navigate(['/app/daily-checkin']);
  }
  navigateToWidgetSettings() {
    this.router.navigate(['/app/widget-settings']);
  }

  get hasCompletedScan(): boolean {
    return !!this.user?.scans?.some((s: any) => s.status === 'Completed');
  }

  async restorePurchases(): Promise<void> {
    if (this.restoring) return;
    this.restoring = true;
    this.restoreMessage = '';
    try {
      const r = await this.storeBilling.restoreDetailed(true);
      this.restoreMessage = this.translationService.translate(
        r === 'restored' ? 'payment.store.restoreDone' : r === 'otherAccount' ? 'payment.store.otherAccount' : 'payment.store.restoreNone',
      );
      if (r === 'restored') void this.syncAccount();
    } finally {
      this.restoring = false;
    }
  }
}
